import { Rocket, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { StatusChip } from '../components/Avatar';
import { CopyButton } from '../components/CopyButton';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  formatAbsoluteDate,
  formatArtifactSummary,
  formatRelativeDate,
  getArtifactTechnicalId,
} from '../utils/formatters';

function PromotionsPage() {
  const {
    workspace,
    sortedPromotions,
    artifactById,
    releaseAppById,
    releaseAppVersionById,
    visibleEnvironments,
    isEnvironmentVisible,
  } = useWorkspace();
  const { channels } = workspace;
  const [query, setQuery] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('all');

  const rows = useMemo(
    () =>
      sortedPromotions
        .filter((promotion) => isEnvironmentVisible(promotion.environment))
        .map((promotion) => {
        let channel = promotion.releaseChannelId
          ? channels.find(
              (candidate) =>
                candidate.releaseChannelId === promotion.releaseChannelId
            )
          : undefined;

        if (!channel) {
          const legacyMatches = channels.filter(
            (candidate) =>
              candidate.environment === promotion.environment &&
              candidate.channel === promotion.channel
          );
          if (legacyMatches.length === 1) {
            channel = legacyMatches[0];
          }
        }

        const version = channel?.releaseAppVersionId
          ? releaseAppVersionById.get(channel.releaseAppVersionId)
          : undefined;
        const app = channel?.releaseAppId
          ? releaseAppById.get(channel.releaseAppId)
          : version
            ? releaseAppById.get(version.releaseAppId)
            : undefined;
        const artifact = artifactById.get(promotion.releaseArtifactId);

        return { promotion, channel, version, app, artifact };
      }),
    [
      artifactById,
      channels,
      releaseAppById,
      releaseAppVersionById,
      sortedPromotions,
      isEnvironmentVisible,
    ]
  );

  const visibleRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return rows.filter(({ promotion, version, app, artifact }) => {
      if (
        environmentFilter !== 'all' &&
        promotion.environment !== environmentFilter
      ) {
        return false;
      }
      if (!normalizedQuery) return true;
      return [
        app?.name,
        app?.platform,
        version?.version,
        promotion.environment,
        promotion.channel,
        artifact?.version,
        artifact?.buildId,
      ].some((value) => value?.toLowerCase().includes(normalizedQuery));
    });
  }, [environmentFilter, query, rows]);

  return (
    <SectionCard
      label="Deployment history"
      title="Every target change"
      description={`${rows.length} visible deployment event${rows.length === 1 ? '' : 's'}, newest first`}
      action={
        rows.length > 0 ? (
          <div className="bundleToolbar">
            <label className="bundleSearch">
              <Search size={14} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search app, version, environment, or bundle"
                aria-label="Search deployments"
              />
            </label>
            <select
              value={environmentFilter}
              onChange={(event) => setEnvironmentFilter(event.target.value)}
              aria-label="Filter deployments by environment"
            >
              <option value="all">All environments</option>
              {visibleEnvironments.map((environment) => (
                <option key={environment.tenantEnvironmentId} value={environment.slug}>
                  {environment.name}
                </option>
              ))}
            </select>
          </div>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <EmptyState
          icon={<Rocket size={20} />}
          title={sortedPromotions.length > 0 ? 'No visible deployment history' : 'No deployments yet'}
          description={
            sortedPromotions.length > 0
              ? 'Show an environment to include its retained deployment history.'
              : 'Deploy a bundle to an app version and environment. Its history will appear here.'
          }
        />
      ) : visibleRows.length === 0 ? (
        <EmptyState
          compact
          icon={<Search size={18} />}
          title="No matching deployments"
          description="Try another app, version, environment, or bundle."
          action={
            <button
              type="button"
              className="secondaryButton"
              onClick={() => {
                setQuery('');
                setEnvironmentFilter('all');
              }}
            >
              Clear filters
            </button>
          }
        />
      ) : (
        <div className="releaseTableWrap" role="region" aria-label="Deployment history table" tabIndex={0}>
          <table className="releaseTable releaseTableHistory">
            <thead>
              <tr>
                <th>App</th>
                <th>Store version</th>
                <th>Environment</th>
                <th>Deployed bundle</th>
                <th>When</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(
                ({ promotion, channel, version, app, artifact }) => (
                  <tr key={promotion.releasePromotionId}>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{app?.name || 'App not recorded'}</strong>
                        <span>
                          {app?.platform?.toUpperCase() ||
                            (channel ? 'Platform unavailable' : 'Legacy deployment event')}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{version?.version || promotion.channel}</strong>
                        <span>
                          {channel
                            ? `${app?.platform?.toUpperCase() || 'App'} target`
                            : 'Exact target ID was not recorded'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`envPill env-${promotion.environment}`}>
                        {promotion.environment}
                      </span>
                    </td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{formatArtifactSummary(artifact)}</strong>
                        {artifact ? (
                          <span
                            className="releaseCodeLine"
                            title={getArtifactTechnicalId(artifact)}
                          >
                            <code>{getArtifactTechnicalId(artifact)}</code>
                            <CopyButton
                              value={getArtifactTechnicalId(artifact)}
                              label="Copy exact build ID"
                            />
                          </span>
                        ) : (
                          <span>Bundle record unavailable</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="releaseIdentity">
                        <strong>{formatRelativeDate(promotion.created)}</strong>
                        <span>{formatAbsoluteDate(promotion.created)}</span>
                      </div>
                    </td>
                    <td>
                      <StatusChip tone="neutral">
                        {promotion.actorType === 'api_key' ? 'Automation' : 'User'}
                      </StatusChip>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

export { PromotionsPage };
